import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { PerspectiveSwitcher } from '../components/PerspectiveSwitcher';
import RiskAssessmentHUD from '../components/RiskAssessmentHUD';
import PursuitChatLauncher from '../components/pursuit/PursuitChatLauncher';
import PursuitLifecycleStepper from '../components/pursuit/PursuitLifecycleStepper';
import PolicyAcceptanceModal from '../components/modals/PolicyAcceptanceModal';
import WelcomeTourModal from '../components/modals/WelcomeTourModal';
import InactivityGuard from '../components/InactivityGuard';
import AppFooter from '../components/AppFooter';
import TrialEndBanner from '../components/TrialEndBanner';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useRiskAssessment } from '../contexts/RiskAssessmentContext';
import { usePendingCheckoutToast } from '../lib/pendingCheckout';
import { LATEST_POLICY_VERSION, APP_VERSION, LEGAL_PAGES_HIDDEN } from '../lib/version';
import { PursuitLayoutContext } from '../contexts/PursuitLayoutContext';

export default function DashboardLayout() {
  const { isAuthenticated, isCheckingAuth, checkSession, isTrialExpired, user, authToken } = useAuth();
  const { showHUD, hideHUD, clearRiskState } = useRiskAssessment();
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedSession = useRef(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  // Pending Stripe checkout toast — fires once per dashboard mount if the
  // user closed the return page mid-poll.
  usePendingCheckoutToast(authToken);
  
  // Only show perspective switcher bar for Super Admin
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  // v3.18.27 — dark-chrome routes (Sentinel currently). The global top
  // bar + AppFooter switch to a dark variant so the workspace chrome
  // doesn't slice a bright band into a dark page.
  const isDarkRoute = /\/sentinel(\/|$)/.test(location.pathname);

  // Pursuit focus mode — when inside a specific pursuit page, collapse the
  // sidebar to a slim rail and promote the lifecycle stepper into the top bar
  // so the user has maximum workspace width while navigating pursuit steps.
  const isPursuitMode = /\/pursuit\/[^/]+\/[^/]/.test(location.pathname);


  // Check if policy acceptance is needed:
  //   - user has never accepted (legacy null), OR
  //   - their stored policy_version is older than LATEST_POLICY_VERSION
  //     (re-acceptance after a Terms/Privacy bump).
  // Suppressed entirely while LEGAL_PAGES_HIDDEN is on, since the modal's
  // "View Terms" / "View Privacy" buttons would 404.
  useEffect(() => {
    if (LEGAL_PAGES_HIDDEN) return;
    if (!isAuthenticated || !user) return;
    const accepted = !!user.policyAcceptedAt;
    const onLatest = user.policyVersion === LATEST_POLICY_VERSION;
    if (!accepted || !onLatest) {
      setShowPolicyModal(true);
    }
  }, [isAuthenticated, user]);

  const handlePolicyAccepted = () => {
    setShowPolicyModal(false);
    // User context is already updated by the modal, no need to reload
  };
  
  // Control HUD visibility based on route.
  // The HUD is per-pursuit (Fit Score + alerts for ONE specific pursuit).
  // It must NEVER show on list/aggregate pages like `/pipeline` or
  // `/pursuits`, otherwise the React-memory `currentPursuit` from a
  // previous navigation (or, worse, a previous user in the same tab)
  // would leak across tenants.
  //
  // v3.11.19 — Restricted to the two screens where the AI co-pilot
  // surface is actually useful: Opportunity Intake (data capture)
  // and Document Ingestion (Manage Documents). Other pursuit screens
  // (Go/No-Go, Gap Analysis, Compliance Tracker, Proposal Workspace,
  // Coverage Map) already render their own Fit Score / signal panels,
  // so the floating HUD becomes redundant chrome there.
  useEffect(() => {
    const path = location.pathname;
    const HUD_ALLOWED_SEGMENTS = /\/pursuit\/[^/]+\/(intake|ingestion)(\/|$|\?)/;
    const inHudContext = HUD_ALLOWED_SEGMENTS.test(path);
    const inPursuitContext = /\/pursuit\/[^/]+(\/|$)/.test(path);

    if (inHudContext) {
      showHUD();
    } else {
      hideHUD();
      // Only wipe in-memory pursuit state when we leave the pursuit
      // entirely (going to pipeline/dashboard/settings). Navigating
      // between sibling pursuit screens (e.g., Intake → Go/No-Go)
      // should keep the pursuit state hot so the next allowed screen
      // re-renders the HUD instantly.
      if (!inPursuitContext) {
        clearRiskState();
      }
    }
  }, [location.pathname, showHUD, hideHUD, clearRiskState]);

  useEffect(() => {
    // Wait for the async /auth/me bootstrap in AuthContext to finish before
    // deciding whether to redirect to /login. Prevents a flash of /login when
    // the user has a valid HttpOnly cookie but no cached profile (fresh browser
    // tab, cleared localStorage, etc.).
    if (isCheckingAuth) return;

    // Only check session once on mount
    if (!hasCheckedSession.current) {
      hasCheckedSession.current = true;
      const userData = checkSession();
      if (!userData && !isAuthenticated) {
        navigate('/login');
      }
    }
  }, [checkSession, isAuthenticated, isCheckingAuth, navigate]);

  // Trial expiry guard - redirect to /trial-expired if trial has expired
  useEffect(() => {
    if (isAuthenticated && isTrialExpired()) {
      navigate('/trial-expired', { replace: true });
    }
  }, [isAuthenticated, isTrialExpired, navigate]);

  // Super Admin route guard - /admin/* routes are ONLY for Super Admin
  useEffect(() => {
    if (isAuthenticated && user && location.pathname.startsWith('/admin')) {
      if (user.role !== ROLES.SUPER_ADMIN) {
        // Non-Super Admin trying to access /admin routes - redirect to their workspace
        const workspaceId = user.organizationId || user.workspaceId || 'home';
        navigate(`/${workspaceId}/dashboard`, { replace: true });
      }
    }
  }, [isAuthenticated, user, location.pathname, navigate]);

  // While the initial auth bootstrap is in flight, show a neutral loading state
  // instead of flashing /login or unauthenticated UI.
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="auth-loading">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  // If not authenticated after initial check, redirect to login
  // But only if we're not already navigating away (logout case)
  if (!isAuthenticated && hasCheckedSession.current) {
    return null;
  }

  return (
    <PursuitLayoutContext.Provider value={{ topBarActive: isPursuitMode }}>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {/* Auto-logout on inactivity: warns at 5 min idle, signs out at 10 min */}
        <InactivityGuard />

        <Sidebar />

        {/* Risk Assessment HUD - Floating Co-Pilot */}
        <RiskAssessmentHUD />

        {/* v3.18.18 — Per-pursuit "Ask this Pursuit" FAB. Only visible on
            /:workspace/pursuit/:id/* routes; auto-closes if user navigates
            out of pursuit context. */}
        <PursuitChatLauncher />

        {/* Policy Acceptance Modal - blocks UI until accepted */}
        <PolicyAcceptanceModal
          isOpen={showPolicyModal}
          onAccepted={handlePolicyAccepted}
        />

        {/* Welcome Tour Modal - for first-time guest users */}
        <WelcomeTourModal />

        {/* Main content area — the sidebar is always the narrow icon rail now,
            so content always gets the slim margin regardless of role. */}
        <main className={`${isPursuitMode ? 'main-content-pursuit' : 'main-content-collapsed'} flex-1 flex flex-col`}>
          {/* Trial-end conversion banner */}
          <TrialEndBanner />

          {/* Top header bar */}
          <div className={
            isDarkRoute
              ? 'bg-slate-900 border-b border-slate-700/50 h-12 flex items-center gap-3 px-4'
              : 'bg-white border-b border-slate-200 h-12 flex items-center gap-3 px-4'
          }>
            {/* Pursuit focus mode: stepper fills the bar */}
            {isPursuitMode && (
              <div className="flex-1 flex items-center min-w-0">
                <PursuitLifecycleStepper isTopBar />
              </div>
            )}

            {/* Non-pursuit: spacer pushes pills right */}
            {!isPursuitMode && <div className="flex-1" />}

            {isSuperAdmin && <PerspectiveSwitcher />}
            {isSuperAdmin && (
              <span
                className="text-[11px] font-mono text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 select-none"
                title="FedBD platform version."
                data-testid="app-version-pill"
              >
                v{APP_VERSION}
              </span>
            )}
          </div>

          <div className="page-enter flex-1">
            <Outlet />
          </div>

          {/* Global App Footer */}
          <AppFooter />
        </main>
      </div>
    </PursuitLayoutContext.Provider>
  );
}
